import { useState, useCallback } from 'react';
import type { Customer, Address } from '../types';
import {
    fetchCustomersFromDb,
    createCustomerInDb,
    updateCustomerInDb,
    fetchAddressesForCustomerFromDb,
    createAddressInDb,
    setCustomerPrimaryAddressInDb,
    type CreateCustomerPayload,
    type UpdateCustomerPayload,
    type CreateAddressPayload,
} from '../services/customerService';
import { getErrorMessage } from '../utils/errors';

export function useCustomers() {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState<string | null>(null);

    const fetchCustomers = useCallback(async (): Promise<Customer[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchCustomersFromDb();
            setCustomers(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch customers'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createCustomer = useCallback(async (
        payload: CreateCustomerPayload
    ): Promise<Customer | null> => {
        setError(null);
        try {
            const created = await createCustomerInDb(payload);
            setCustomers(prev => [...prev, created]);
            return created;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create customer'));
            return null;
        }
    }, []);

    const updateCustomer = useCallback(async (
        id: string,
        patch: UpdateCustomerPayload
    ): Promise<Customer | null> => {
        setError(null);
        try {
            const updated = await updateCustomerInDb(id, patch);
            setCustomers(prev => prev.map(c => String(c.id) === id ? updated : c));
            return updated;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update customer'));
            return null;
        }
    }, []);

    const fetchAddressesForCustomer = useCallback(async (
        customerId: string
    ): Promise<Address[]> => {
        try {
            return await fetchAddressesForCustomerFromDb(customerId);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch addresses'));
            return [];
        }
    }, []);

    const createAddress = useCallback(async (
        payload: CreateAddressPayload
    ): Promise<Address | null> => {
        setError(null);
        try {
            return await createAddressInDb(payload);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create address'));
            return null;
        }
    }, []);

    const setCustomersPrimaryAddress = useCallback(async (
        customerId: string,
        addressId: string
    ): Promise<boolean> => {
        setError(null);
        try {
            await setCustomerPrimaryAddressInDb(customerId, addressId);
            setCustomers(prev => prev.map(c =>
                String(c.id) === customerId
                    ? { ...c, delivery_address: addressId }
                    : c
            ));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to set primary address'));
            return false;
        }
    }, []);

    return {
        customers,
        loading,
        error,
        fetchCustomers,
        createCustomer,
        updateCustomer,
        fetchAddressesForCustomer,
        createAddress,
        setCustomersPrimaryAddress,
    };
}